import React, { useEffect, useRef, useState } from 'react';
import { Loader2 } from 'lucide-react';
import { cn } from '@/lib/utils';
import { useLocale } from '../contexts/LocaleContext';
import { loadingTextBase } from './DocumentLoadingScreen';

const LOADING_DOTS_INTERVAL_MS = 450;

interface SynonymsPopupProps {
  isOpen: boolean;
  position: { top: number; left: number };
  synonyms: string[];
  loading: boolean;
  wordCount: number;
  onSelect: (synonym: string) => void;
  onClose: () => void;
}

interface SynonymListProps {
  items: string[];
  highlightedIndex: number;
  onHighlight: (index: number) => void;
  onSelect: (synonym: string) => void;
}

interface SynonymsLoadingStateProps {
  label: string;
}

const SynonymsLoadingState: React.FC<SynonymsLoadingStateProps> = ({ label }) => {
  const baseText = loadingTextBase(label);
  const [dotCount, setDotCount] = useState(1);

  useEffect(() => {
    const intervalId = window.setInterval(() => {
      setDotCount((current) => (current >= 3 ? 1 : current + 1));
    }, LOADING_DOTS_INTERVAL_MS);

    return () => window.clearInterval(intervalId);
  }, []);

  return (
    <div
      className="flex items-center justify-center gap-2 px-4 py-3 text-sm text-muted-foreground"
      role="status"
      aria-live="polite"
      aria-label={label}
      data-testid="synonyms-loading"
    >
      <Loader2 className="h-4 w-4 shrink-0 animate-spin" aria-hidden />
      <span className="relative inline-block" data-testid="loading-dots">
        <span data-testid="loading-dots-label">{baseText}</span>
        <span
          className="absolute left-full top-0 inline-block w-[1.5em] text-left"
          aria-hidden="true"
          data-testid="loading-dots-ellipsis"
        >
          {'.'.repeat(dotCount)}
        </span>
      </span>
    </div>
  );
};

const SynonymList: React.FC<SynonymListProps> = ({
  items,
  highlightedIndex,
  onHighlight,
  onSelect,
}) => (
  <ul role="listbox" className="m-0 list-none p-0">
    {items.map((item, index) => {
      const isHighlighted = index === highlightedIndex;
      return (
        <li key={index} role="presentation">
          <button
            type="button"
            role="option"
            data-index={index}
            aria-selected={isHighlighted}
            tabIndex={isHighlighted ? 0 : -1}
            onClick={() => onSelect(item)}
            onMouseEnter={() => onHighlight(index)}
            className={cn(
              'w-full border-0 border-border px-4 py-3 text-left text-sm text-foreground outline-none transition-colors',
              'focus:outline-none focus-visible:outline-none focus-visible:ring-0',
              index < items.length - 1 && 'border-b',
              isHighlighted
                ? 'bg-neutral-200 font-medium dark:bg-neutral-700'
                : 'bg-transparent',
            )}
          >
            {item}
          </button>
        </li>
      );
    })}
  </ul>
);

export const SynonymsPopup: React.FC<SynonymsPopupProps> = ({
  isOpen,
  position,
  synonyms,
  loading,
  wordCount,
  onSelect,
  onClose,
}) => {
  const { t } = useLocale();
  const popupRef = useRef<HTMLDivElement>(null);
  const [highlightedIndex, setHighlightedIndex] = useState(0);
  const [adjustedPosition, setAdjustedPosition] = useState<{ top: number; left: number; transform: string }>({
    top: position.top,
    left: position.left,
    transform: 'translateX(-50%)',
  });

  const hasSynonyms = !loading && synonyms.length > 0;
  const loadingLabel = wordCount > 1 ? t('loading_alternate_wordings') : t('loading_synonyms');

  useEffect(() => {
    if (!isOpen || !hasSynonyms) {
      return;
    }

    setHighlightedIndex(0);

    const focusHighlightedOption = (): void => {
      const option = popupRef.current?.querySelector<HTMLElement>('[data-index="0"]');
      option?.focus();
    };

    const timeoutId = window.setTimeout(focusHighlightedOption, 0);
    return () => window.clearTimeout(timeoutId);
  }, [isOpen, hasSynonyms, synonyms]);

  useEffect(() => {
    if (!isOpen || !hasSynonyms) {
      return;
    }

    const option = popupRef.current?.querySelector<HTMLElement>(`[data-index="${highlightedIndex}"]`);
    if (typeof option?.scrollIntoView === 'function') {
      option.scrollIntoView({ block: 'nearest' });
    }
    option?.focus();
  }, [highlightedIndex, isOpen, hasSynonyms]);

  useEffect(() => {
    if (!isOpen) {
      return;
    }

    const handleClickOutside = (event: MouseEvent): void => {
      if (popupRef.current && !popupRef.current.contains(event.target as Node)) {
        onClose();
      }
    };

    const handleKeyDown = (event: KeyboardEvent): void => {
      if (event.key === 'Escape') {
        event.preventDefault();
        event.stopPropagation();
        onClose();
        return;
      }

      if (!hasSynonyms) {
        return;
      }

      if (event.key === 'ArrowDown') {
        event.preventDefault();
        setHighlightedIndex((current) => (current + 1) % synonyms.length);
        return;
      }

      if (event.key === 'ArrowUp') {
        event.preventDefault();
        setHighlightedIndex((current) =>
          current <= 0 ? synonyms.length - 1 : current - 1,
        );
        return;
      }

      if (event.key === 'Enter') {
        event.preventDefault();
        const synonym = synonyms[highlightedIndex];
        if (synonym) {
          onSelect(synonym);
        }
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    document.addEventListener('keydown', handleKeyDown);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
      document.removeEventListener('keydown', handleKeyDown);
    };
  }, [isOpen, onClose, hasSynonyms, synonyms, highlightedIndex, onSelect]);

  useEffect(() => {
    if (!isOpen) {
      return;
    }

    const adjustPosition = (): void => {
      if (!popupRef.current) {
        return;
      }

      const popup = popupRef.current;
      const viewportWidth = window.innerWidth;
      const viewportHeight = window.innerHeight;
      const padding = 8;

      let left = position.left;
      let top = position.top;
      let transform = 'translateX(-50%)';

      const rect = popup.getBoundingClientRect();
      const popupWidth = rect.width;
      const popupHeight = rect.height;
      const halfWidth = popupWidth / 2;

      if (left - halfWidth < padding) {
        left = padding + halfWidth;
      } else if (left + halfWidth > viewportWidth - padding) {
        left = viewportWidth - padding - halfWidth;
      }

      if (top + popupHeight > viewportHeight - padding) {
        const spaceAbove = top - padding;
        const spaceBelow = viewportHeight - top - padding;
        if (spaceAbove > spaceBelow && spaceAbove >= Math.min(popupHeight, 400)) {
          top = top - popupHeight - 10;
        } else {
          top = Math.max(padding, viewportHeight - popupHeight - padding);
        }
      }

      if (top < padding) {
        top = padding;
      }

      setAdjustedPosition({ top, left, transform });
    };

    const timeoutId = setTimeout(adjustPosition, 0);
    const resizeObserver = new ResizeObserver(adjustPosition);
    const handleResize = (): void => {
      adjustPosition();
    };

    window.addEventListener('resize', handleResize);
    window.addEventListener('scroll', handleResize, true);

    if (popupRef.current) {
      resizeObserver.observe(popupRef.current);
    }

    return () => {
      clearTimeout(timeoutId);
      resizeObserver.disconnect();
      window.removeEventListener('resize', handleResize);
      window.removeEventListener('scroll', handleResize, true);
    };
  }, [isOpen, position, synonyms, loading]);

  if (!isOpen) {
    return null;
  }

  return (
    <div
      ref={popupRef}
      data-editor-overlay=""
      className="fixed z-[1001] max-h-[400px] min-w-[200px] max-w-[300px] overflow-y-auto rounded-lg border border-border bg-popover text-popover-foreground shadow-lg"
      style={{
        top: `${adjustedPosition.top}px`,
        left: `${adjustedPosition.left}px`,
        transform: adjustedPosition.transform,
      }}
      onMouseDown={(e) => {
        e.preventDefault();
        e.stopPropagation();
      }}
    >
      {loading ? (
        <SynonymsLoadingState label={loadingLabel} />
      ) : synonyms.length === 0 ? (
        <div className="px-4 py-3 text-center text-sm text-muted-foreground">
          {t('no_synonyms_found')}
        </div>
      ) : (
        <SynonymList
          items={synonyms}
          highlightedIndex={highlightedIndex}
          onHighlight={setHighlightedIndex}
          onSelect={onSelect}
        />
      )}
    </div>
  );
};
